import React, { useContext, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import LabelName from 'components/UI/LabelName/LabelName';

import { handleDateTimeOffset } from 'utils/utility_functions/timezone';
import { UserDetailsContext } from 'context/userDetailsContext';

import constants from 'constants/index';
import { downloadAttachments } from 'services/calendarServices';
import { generateFullName } from 'utils/generateFullNameUtils';
import { Tooltip } from 'primereact/tooltip';
import { classNames } from 'primereact/utils';
import { GroupUserTooltip } from 'components/UI/GroupUserTooltip/GroupUserTooltip';
import { useCalendarContext } from 'modules/calendar/Context/CalendarContext';
import ImageComponent from 'shared/ImageComponent';
import { getProfileImageFilePath, parseReminderData, setAppTitle } from 'utils/utils';
import { useConferenceRooms } from 'hooks/useConferenceRooms';
import { Menu } from 'primereact/menu';
import { clearLocalStorageItems, handleSettingSkAndPkToLocalStorage } from 'utils/localStorageUtils';
import { reminderChannels, reminderOptions } from 'constants/dropdownOptions';
import AttendeesPreview from './AttendeesPreview';
import { InputSwitch } from 'primereact/inputswitch';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';

function EventAndTaskPreview({
  eventDetails,
  setCancelEvent,
  handleDeleteRecurringEvent,
  handleEdit,
  cancelEventPermission,
  editEventPermission,
}) {
  const userContext = useContext(UserDetailsContext);

  const { isClientCalendar, setEditAllRecurringEvent, isGlobal } = useCalendarContext();
  const { conferenceRoomOptions } = useConferenceRooms();

  const history = useHistory();

  const opanel = useRef();

  // Function to get conference room name by ID
  const getConferenceRoomName = (roomId) => {
    if (!roomId || !conferenceRoomOptions) return null;
    const room = conferenceRoomOptions.find((room) => room.value === roomId);
    return room?.label || null;
  };

  // Function to check if conference room still exists in current list
  const isConferenceRoomValid = (roomId) => {
    if (!roomId || !conferenceRoomOptions) return false;
    return conferenceRoomOptions.some((room) => room.value === roomId);
  };

  const isFirmWide = Boolean(eventDetails?.firm_wide);
  const isFirmAdmin = userContext?.access_level === 'firm-admin' || userContext?.userDetails?.adminPrivileges;

  const items = [
    {
      label: 'Edit Event',
      command: () => {
        setEditAllRecurringEvent('');
        handleEdit();
      },
      disabled: !editEventPermission || (isFirmWide && !isFirmAdmin),
    },
    {
      label: 'Delete Event',
      command: () => {
        setEditAllRecurringEvent('');
        setCancelEvent(true);
      },
      disabled: !cancelEventPermission || (isFirmWide && !isFirmAdmin),
    },
  ];
  const itemsRecurring = [
    {
      label: 'Edit Event',
      command: () => {
        handleEdit();
      },
      disabled: !editEventPermission || (isFirmWide && !isFirmAdmin),
    },
    {
      label: 'Delete Event',
      command: () => {
        handleDeleteRecurringEvent();
      },
      disabled: !cancelEventPermission || (isFirmWide && !isFirmAdmin),
    },
  ];

  const onClickAttachments = (e, fileInfo) => {
    e.preventDefault();
    downloadAttachments(fileInfo)
      .then((data) => {
        let link = document.createElement('a');
        link.download = fileInfo.display_name;
        let url = window.URL.createObjectURL(data.Body);
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const navigateToModule = (item, module) => {
    if (!isClientCalendar) {
      clearLocalStorageItems(['pk', 'sk']);

      let url = '/';
      if (module === 'cases') {
        if (item.is_lead) {
          url = item.case_id ? `/leads/${item.case_id}` : '/leads';
        } else {
          url = item.case_id ? `/cases/${item.case_id}` : '/cases';
        }
      } else if (module === 'groups') {
        url = '/settings/groups';
      } else if (module === 'users') {
        if (item?.access_level && item?.access_level !== 'client') {
          url = item?.firm_user_id ? `/settings/team-management/${item?.firm_user_id}` : '/settings/team-management';
        } else {
          url = item?.contact_id ? `/contacts/${item?.contact_id}` : '/contacts';
        }
      }

      return url;
    }
  };
  const getStatusLabel = (option) => {
    if (option) {
      if (option?.is_confirmed === false && (option?.status === 'ACTIVE' || option?.status === undefined)) {
        return ' (Unconfirmed)';
      } else if ((option?.is_confirmed || option?.is_confirmed === undefined) && option?.status === 'ARCHIVED') {
        return ' (Archived)';
      } else if (option?.is_confirmed === false && option?.status === 'ARCHIVED') {
        return ' (Archived)';
      }
    }
    return '';
  };

  function AttendeeList({ users, groups }) {
    if ((!users || users.length === 0) && (!groups || groups.length === 0)) {
      return <div>No attendees</div>;
    }
    return (
      <>
        {groups?.map((group, index) => (
          <div className="d-flex mb-1" key={index}>
            {isClientCalendar ? (
              <div
                className={classNames({
                  pointer: false,
                  ['name-' + index]: true,
                  'align-items-center d-flex pe-none black-700': true,
                  'text-decoration-none': true,
                })}
              >
                <ImageComponent filePath={group?.profile_image} fileName={group?.group_name} fileSize="small.jpg" />
                {group?.group_name} {group?.status === 'ARCHIVED' && '(Archived)'}
              </div>
            ) : (
              <Link
                to={() => navigateToModule(group, 'groups')}
                className={classNames({
                  pointer: true,
                  // 'link-primary': (!isClientCalendar && group?.status !== 'Deleted') || group?.status !== 'Deleted',
                  'text-primary-dark': !isClientCalendar,
                  ['name-' + index]: true,
                  'align-items-center d-flex': true,
                  'text-decoration-none': true,
                })}
              >
                <ImageComponent filePath={group?.profile_image} fileName={group?.group_name} fileSize="small.jpg" />{' '}
                <Tooltip target={`.name-${index}`} position="top">
                  {GroupUserTooltip(group, group?.firm_user_data?.length ? group?.firm_user_data : users)}
                </Tooltip>
                {group?.group_name} {group?.status === 'ARCHIVED' && '(Archived)'}
              </Link>
            )}
          </div>
        ))}
        {users.map((item, index) => (
          <div className="d-flex mb-1" key={index}>
            {isClientCalendar ? (
              <div
                className={classNames({
                  ' align-items-center d-flex': true,
                  'text-decoration-none': true,
                  'd-flex mb-1': true,
                  pointer: false,
                })}
              >
                <ImageComponent
                  filePath={item?.profile_image || getProfileImageFilePath(item?.firm_user_id || item?.contact_id)}
                  fileName={item.name}
                  fileSize="small.jpg"
                  style={{ backgroundColor: '#2196F3', color: '#ffffff' }}
                />
                {generateFullName(item) || item?.name || item?.assignee_name}
                {getStatusLabel(item)}
              </div>
            ) : (
              <>
                {/* <i className="icon-calendar me-2 mt-1"></i> */}
                <Link
                  to={() => navigateToModule(item, 'users')}
                  className={classNames({
                    // 'link-primary pointer': !isClientCalendar,
                    'text-primary-dark pointer': !isClientCalendar,
                    ' align-items-center d-flex': true,
                    'text-decoration-none': true,
                    'd-flex mb-1': true,
                  })}
                >
                  <ImageComponent
                    filePath={item?.profile_image || getProfileImageFilePath(item?.firm_user_id || item?.contact_id)}
                    fileName={item.name}
                    fileSize="small.jpg"
                    style={{ backgroundColor: '#2196F3', color: '#ffffff' }}
                  />
                  <>
                    {item?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                    {generateFullName(item) || item?.name || item?.assignee_name}
                    {getStatusLabel(item)}
                  </>
                </Link>
              </>
            )}
          </div>
        ))}
      </>
    );
  }

  useEffect(() => {
    // set the app tab title
    setAppTitle('Calendar', eventDetails?.title || '');

    // clean up function
    return () => {
      setAppTitle('Calendar');
    };
  }, [eventDetails]);

  return (
    <>
      {eventDetails ? (
        <form>
          {/* form heading */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="modal-heading F-size16 text-bold">{eventDetails?.res_type === 'event' ? 'Event Details' : 'Task Details'}</div>
            {eventDetails?.pk && eventDetails?.res_type === 'event' && eventDetails?.upcoming_event?.type !== 'SOL' && !isClientCalendar ? (
              <>
                <div className="pointer">
                  <div
                    className="d-flex border border-1 rounded  justify-content-center p-2 task-lane-header-options pointer"
                    onClick={(e) => opanel?.current?.toggle(e)}
                  >
                    <i className="pi pi-ellipsis-v d-flex align-items-center" />
                  </div>
                </div>
                <Menu model={eventDetails?.is_recurring ? itemsRecurring : items} popup ref={opanel} id="popup_menu" />
              </>
            ) : null}
          </div>
          {/* title */}
          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-md-4 col-12 ">
              <LabelName required={false} htmlFor="title">
                {eventDetails?.res_type === 'event' ? 'Event Title' : 'Task Title'}
              </LabelName>
            </div>
            {/* <div className="col-md-8 col-12 input-style"> */}
            <div className="col-md-8 col-12">{eventDetails?.res_type === 'event' ? eventDetails?.title : eventDetails?.task_name}</div>
          </div>
          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-md-4 col-12">
              <LabelName required={false} htmlFor="title">
                Case
              </LabelName>
            </div>
            <div className="col-md-8 col-12">
              <div className="mb-2 casename-container text-center F-size13 m-0 p-0">
                {eventDetails?.case_name ? (
                  <Link
                    className="pointer mb-0 p-2 react-tagsinput-tag text-decoration-none"
                    to={() => navigateToModule(eventDetails, 'cases')}
                  >
                    {eventDetails?.case_name}
                  </Link>
                ) : (
                  'No case selected'
                )}
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-md-4 col-12">
              <LabelName required={false} htmlFor="title">
                Event Type
              </LabelName>
            </div>
            <div className="col-md-8 col-12">
              {eventDetails?.event_type?._event_type_icon_ && <i className={eventDetails?.event_type?._event_type_icon_ + ' me-2'}></i>}
              {eventDetails?.event_type?._event_type_ || 'No event type'}
            </div>
          </div>
          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-md-4 col-12">
              <LabelName required={false} htmlFor="title">
                Date
              </LabelName>
            </div>
            <div className="col-md-8 col-12">
              {eventDetails?.res_type === 'event' && eventDetails?.all_day
                ? (() => {
                    const startDate = handleDateTimeOffset(
                      userContext.userDetails.timezone,
                      eventDetails?.meeting_start_time,
                      constants.month_date_year_format
                    );
                    const endDate = handleDateTimeOffset(
                      userContext.userDetails.timezone,
                      eventDetails?.meeting_end_time,
                      constants.month_date_year_format
                    );
                    return startDate !== endDate ? `${startDate} - ${endDate}` : startDate;
                  })()
                : handleDateTimeOffset(
                    userContext.userDetails.timezone,
                    eventDetails?.res_type === 'event' ? eventDetails?.meeting_start_time : eventDetails?.due_date,
                    constants.month_date_year_format
                  )}
            </div>
          </div>

          {eventDetails?.res_type === 'event' && (
            <>
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12">
                  <LabelName required={false} htmlFor="title">
                    Time
                  </LabelName>
                </div>
                <div className="col-md-8 col-12 d-flex align-items-center">
                  {eventDetails?.all_day ? (
                    <div>All Day</div>
                  ) : (
                    <>
                      <div>
                        {handleDateTimeOffset(
                          userContext.userDetails.timezone,
                          eventDetails?.meeting_start_time,
                          constants.hour_minute_12_format
                        )}
                      </div>
                      <div className="text-bold px-4">-</div>
                      <div>
                        {handleDateTimeOffset(
                          userContext.userDetails.timezone,
                          eventDetails?.meeting_end_time,
                          constants.hour_minute_12_format
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12">
                  <LabelName required={false} htmlFor="is_recurring">
                    Recurring Event
                  </LabelName>
                </div>
                <div className="col-md-8 col-12 d-flex align-items-center justify-content-start text-capitalize edit-field">
                  <div className="w-100 ">
                    <InputSwitch
                      name={'is_recurring'}
                      value={eventDetails?.is_recurring}
                      checked={eventDetails?.is_recurring}
                      className="input-swich"
                      placeholder="Recurring Event"
                      disabled={true}
                    />
                  </div>
                </div>
              </div>
              {isGlobal && (
                <div className="d-flex align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12">
                    <LabelName required={false} htmlFor="firm_wide">
                      Firm-Wide Event
                    </LabelName>
                  </div>
                  <div className="col-md-8 col-12 d-flex align-items-center justify-content-start text-capitalize edit-field">
                    <div className="w-100">
                      <InputSwitch
                        name="firm_wide"
                        value={isFirmWide}
                        checked={isFirmWide}
                        className="input-swich"
                        placeholder="Firm-Wide Event"
                        disabled={true}
                      />
                    </div>
                  </div>
                </div>
              )}
              {eventDetails?.is_recurring && (
                <>
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-md-4 col-12">
                      <LabelName required={false} htmlFor="recurrence_limit">
                        Till Date
                      </LabelName>
                    </div>
                    <div className="col-md-8 col-12">
                      {handleDateTimeOffset(
                        userContext.userDetails.timezone,
                        eventDetails?.recurrence_keys.recurrence_limit,
                        constants.month_date_year_format
                      )}
                    </div>
                  </div>
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-md-4 col-12">
                      <LabelName required={false} htmlFor="recurrence_frequency">
                        Schedule
                      </LabelName>
                    </div>
                    <div className="col-md-8 col-12 text-capitalize">{eventDetails?.recurrence_keys.recurrence_frequency}</div>
                  </div>
                  {eventDetails?.recurrence_keys.recurrence_frequency === 'nth_weekday' && (
                    <>
                      <div className="d-flex align-items-center py-2 flex-wrap">
                        <div className="col-md-4 col-12">
                          <LabelName required={false} htmlFor="week_occurrence">
                            Week Occurrence
                          </LabelName>
                        </div>
                        <div className="col-md-8 col-12">{eventDetails?.recurrence_keys.week_occurrence}</div>
                      </div>
                      <div className="d-flex align-items-center py-2 flex-wrap">
                        <div className="col-md-4 col-12">
                          <LabelName required={false} htmlFor="weekday">
                            Weekday
                          </LabelName>
                        </div>
                        <div className="col-md-8 col-12">{eventDetails?.recurrence_keys.weekday}</div>
                      </div>
                    </>
                  )}
                </>
              )}
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12">
                  <LabelName required={false} htmlFor="title">
                    Location
                  </LabelName>
                </div>
                {eventDetails?._office_location_ ? (
                  <div className="col-md-8 col-12">{eventDetails?._office_location_}</div>
                ) : (
                  <div className="col-md-8 col-12"> None</div>
                )}
              </div>
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12">
                  <LabelName required={false} htmlFor="conference_room">
                    Conference Room
                  </LabelName>
                </div>
                <div className="col-md-8 col-12">
                  {(() => {
                    const roomId = eventDetails?.conference_room_id || eventDetails?.conference_room;
                    const isRoomValid = isConferenceRoomValid(roomId);

                    if (!isRoomValid) {
                      return 'None';
                    }

                    return (
                      eventDetails?.room_name ||
                      eventDetails?._conference_room_ ||
                      getConferenceRoomName(eventDetails?.conference_room_id) ||
                      getConferenceRoomName(eventDetails?.conference_room) ||
                      'None'
                    );
                  })()}
                </div>
              </div>
              {eventDetails?.case_name === '' && (
                <div className="d-flex align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12">
                    <LabelName required={false} htmlFor="title">
                      Practice Area
                    </LabelName>
                  </div>
                  {eventDetails?._practice_area_ ? (
                    <div className="col-md-8 col-12">
                      <span>{eventDetails._practice_area_}</span>
                    </div>
                  ) : (
                    <div className="col-md-8 col-12"> None</div>
                  )}
                </div>
              )}
            </>
          )}

          <div className="d-flex py-2 flex-wrap">
            <div className="col-md-4 col-12">
              <LabelName required={false} htmlFor="title">
                Created by
              </LabelName>
            </div>
            <div className="col-md-8 col-12">
              {isClientCalendar ? (
                // 🔹 Non-clickable plain text
                <div className="md-2 align-items-center d-flex ">
                  {eventDetails?.created_by_firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                  {generateFullName(eventDetails?.created_by_details)}
                </div>
              ) : (
                <div className="col-md-8 col-12 redirection-link-no-text-transform">
                  <Link
                    to={{
                      pathname: eventDetails?.created_by_firm_user_id
                        ? `/settings/team-management/${eventDetails?.created_by_firm_user_id}`
                        : '/settings/team-management',
                      state: eventDetails, // Passing state to the target route
                    }}
                    onClick={() => {
                      const { sk, pk } = eventDetails;
                      handleSettingSkAndPkToLocalStorage(sk, pk);
                    }}
                    className="text-decoration-none"
                  >
                    {eventDetails?.created_by_firm_user_id && <i className="fas fa-user-tie me-2"></i>}
                    {generateFullName(eventDetails?.created_by_details)}
                  </Link>
                </div>
              )}
            </div>
          </div>
          <div className="d-flex py-2 flex-wrap">
            <div className="col-md-4 col-12">
              <LabelName required={false} htmlFor="title">
                {eventDetails?.res_type === 'event' ? 'Attendees' : 'Assignees'}
              </LabelName>
            </div>
            <div className="col-md-8 col-12">
              <>
                {eventDetails?.res_type === 'event' ? (
                  <AttendeeList users={eventDetails?.guests} groups={eventDetails?.groups} />
                ) : (
                  <AttendeeList users={eventDetails?.assign_to} groups={eventDetails?.groups} />
                )}
              </>
            </div>
          </div>
          {eventDetails?.res_type === 'event' && (
            <div className="d-flex py-2 flex-wrap">
              <AttendeesPreview
                selectedGroups={eventDetails.groups}
                selectedFirmUsers={eventDetails.firm_users}
                selectedContacts={eventDetails.contacts}
                history={history}
                isClientCalendar={isClientCalendar}
              />
            </div>
          )}
          {eventDetails?.res_type === 'event' && (
            <div className="d-flex py-2 flex-wrap">
              <div className="col-md-4 col-12">
                <LabelName required={false} htmlFor="title">
                  Attachments
                </LabelName>
              </div>
              {eventDetails?.attachments?.length > 0 ? (
                <div className="col-12 col-lg-8">
                  <ul className="px-0">
                    {eventDetails?.attachments?.map((file, index) => {
                      return (
                        <li className="li-style-none d-flex text-break" key={index}>
                          <i className="fas fa-paperclip me-2 mt-1"></i>
                          <a
                            href="/"
                            onClick={(e) => {
                              onClickAttachments(e, file);
                            }}
                          >
                            {file?.file_name}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <div className="col-md-8 col-12">No attachments added</div>
              )}
            </div>
          )}
          {parseReminderData(eventDetails.reminder)?.length > 0 && (
            <div className="d-flex py-2 flex-wrap">
              <div className="col-md-4 col-12">
                <LabelName required={false} htmlFor="reminder">
                  Reminder
                </LabelName>
              </div>
              <div className="col-md-8 col-12">
                {parseReminderData(eventDetails.reminder)?.map((data, index) => {
                  return (
                    <div
                      className="d-flex input-shadow align-items-center justify-content-center p-2 mb-2 bg-grey mt-2"
                      key={data?.reminder_channel_id}
                    >
                      <div className="w-100">
                        <div>{reminderChannels?.find((channel) => channel?.value === data?.reminder_channel_type)?.label}</div>
                        <div>
                          {data?.value === 'custom' ? (
                            <>
                              <div>{data?.value}</div>
                              <div>{`${data?.customValue} ${data?.type}`}</div>
                            </>
                          ) : (
                            reminderOptions.find((channel) => channel.value === data?.value)?.label
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </form>
      ) : (
        <div className="d-flex justify-content-center">Loading....</div>
      )}
    </>
  );
}

export default EventAndTaskPreview;
